import { Suspense, useCallback, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { LayoutProvider, LayoutSplashScreen } from "../_metronic/layout/core";
import { MasterInit } from "../_metronic/layout/MasterInit";
import { ThemeModeProvider } from "../_metronic/partials";
import ToastWrapper from "./components/Toast/Toast";
import { useSelector } from "react-redux";
import { QueryStatus } from "@reduxjs/toolkit/query";
import PageLoader from "./components/PageLoader/PageLoader";
import { RootState } from "../store/store";
import useSignalR from "./services/signalR/useSignalR";
import { useDispatch } from "react-redux";
import { appNotification, selectShowSessionExpiredModal, showSessionExpiredModal } from "./app.slice";
import { signalRNotificationEvent } from "./services/signalR/signalREvent";
import { ISignalREventPayload } from "./app.interfaces";
import useNetwork from "./hooks/useNetwork";
import { selectCurrentUser } from "./modules/auth/features/auth.slice";
import { store } from "../store/store";
import { useLazyGetSideBarCounterQuery } from "./modules/auth/services/auth.api";
import FreshdeskWidget from "./services/freshdesk/support";
import useAuth from "./modules/auth/hooks/useAuth";
import useBrowserTabEvent, { BROADCAST_EVENT, sendTabEvent } from "./hooks/useBrowserTabEvent";
import { setEncodedUrl } from "./utils/session";

const isOnAuthScreen = () => ["/auth", "/logout"].some((path) => window.location.pathname.includes(path));

/**
 * The token is a cookie every tab shares, so a 401 in one tab means the session is gone in all of them.
 * Tabs that have not called an API yet would otherwise look signed in until their next request.
 */
const sessionExpiryTabEvents = {
  [BROADCAST_EVENT.SESSION_EXPIRED_EVENT]: {
    eventName: BROADCAST_EVENT.SESSION_EXPIRED_EVENT,
    callback: () => {
      if (isOnAuthScreen() || selectShowSessionExpiredModal(store.getState())) return;
      store.dispatch(showSessionExpiredModal());
    },
  },
};

const App = () => {
  const { online } = useNetwork();
  const { connection } = useSignalR();
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const [getSidebarCounter] = useLazyGetSideBarCounterQuery();
  const isLoading = useSelector((state: RootState) => {
    const skipApis = ["getAdminDashboardStats","getAdminDashboardOrderStats", "respondToTicket", "getSideBarCounter", "getFlashMessageOverview", "getFlashMessageDetail", "getFlashMessageCompare"];
    return Object.values({ ...state.api.mutations, ...state.api.queries }).some((query) => {
      if (query && skipApis.includes(query?.endpointName as string)) return false;
      return query && query.status === QueryStatus.pending;
    });
  });
  const isSessionExpired = useSelector(selectShowSessionExpiredModal);

  useBrowserTabEvent(sessionExpiryTabEvents);

  useEffect(() => {
    // Every tab that newly shows the modal re-broadcasts once; tabs already showing it ignore the echo.
    if (isSessionExpired && !isOnAuthScreen()) sendTabEvent(BROADCAST_EVENT.SESSION_EXPIRED_EVENT);
  }, [isSessionExpired]);

  const onReceiveMessage = useCallback(
    (payload: ISignalREventPayload) => (msg: string) => {
      try {
        const { callback, ...rest } = payload;
        if (callback) callback(rest);
        dispatch(appNotification({ ...rest, msg }));
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!connection) return;
    try {
      Object.keys(signalRNotificationEvent).forEach((key) => {
        let s: ISignalREventPayload = signalRNotificationEvent[key];
        if (key === "logoutUser")
          s = {
            ...s,
            callback: () => {
              const currentPath = window.location.pathname + window.location.search;
              const userId = selectCurrentUser(store.getState())?.user_id;
              setEncodedUrl(currentPath, userId);
              sendTabEvent(BROADCAST_EVENT.LOGOUT_EVENT, { redirectUrl: currentPath });
            },
          };
        if (key === "customerTicketMessage" || key === "ticketCount") {
          s = {
            ...s,
            callback: () => {
              getSidebarCounter();
            },
          };
        }
        connection.on(key, onReceiveMessage(s));
      });
    } catch (error) {
      console.error(error);
    }

    return () => {
      try {
        Object.keys(signalRNotificationEvent).forEach((key) => {
          connection.off(key, onReceiveMessage(signalRNotificationEvent[key]));
        });
      } catch (error) {
        console.error(error);
      }
    };
  }, [connection]);

  return (
    <Suspense fallback={<LayoutSplashScreen />}>
      {!online && (
        <div className="d-flex flex-center bg-danger" style={{ zIndex: 10000 }}>
          <span className="fw-bolder text-white">You are Offline! Please check your Internet connection</span>
        </div>
      )}
      <LayoutProvider>
        <ThemeModeProvider>
          {isLoading && <PageLoader />}
          <Outlet />

          <MasterInit />
          <ToastWrapper />
          {isAuthenticated() && <FreshdeskWidget />}
        </ThemeModeProvider>
      </LayoutProvider>
    </Suspense>
  );
};

export { App };
